import React, { useState } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { RiRobot2Fill, RiCloseLine, RiQuillPenFill, RiFileChartFill, RiFlowChart, RiGroupFill, RiShieldFlashFill } from 'react-icons/ri';
import API_BASE_URL from '../api';

import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const FormattedAIResponse = ({ text }) => {
    if (!text) return null;

    return (
        <div className="ai-response-formatted" style={{ color: '#555', fontSize: '15px', lineHeight: '1.8' }}>
            <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                    table: ({node, ...props}) => (
                        <div style={{ overflowX: 'auto', margin: '20px 0', borderRadius: '12px', border: '1px solid #EAE2D5', boxShadow: '0 4px 12px rgba(0,0,0,0.02)' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '14px' }} {...props} />
                        </div>
                    ),
                    tr: ({node, ...props}) => (
                        <tr style={{ borderBottom: '1px solid #F5F0E8' }} {...props} />
                    ),
                    th: ({node, ...props}) => (
                        <th style={{ padding: '12px 16px', fontWeight: '800', color: '#2C1A10', backgroundColor: '#FDF8F3', borderBottom: '2px solid #EAE2D5', textTransform: 'uppercase', fontSize: '12px', letterSpacing: '0.5px' }} {...props} />
                    ),
                    td: ({node, ...props}) => (
                        <td style={{ padding: '12px 16px', color: '#555', lineHeight: '1.5' }} {...props} />
                    ),
                    h1: ({node, children, ...props}) => <h1 style={{ color: '#2C1A10', fontSize: '22px', fontWeight: '800', marginTop: '20px', marginBottom: '10px' }} {...props}>{children}</h1>,
                    h2: ({node, children, ...props}) => <h2 style={{ color: '#2C1A10', fontSize: '20px', fontWeight: '800', marginTop: '20px', marginBottom: '10px' }} {...props}>{children}</h2>,
                    h3: ({node, ...props}) => (
                        <h3 style={{ color: '#2C1A10', fontSize: '18px', fontWeight: '800', marginTop: '20px', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <span style={{ width: '4px', height: '20px', backgroundColor: '#C0722C', borderRadius: '2px' }}></span>
                            {props.children}
                        </h3>
                    ),
                    code: ({node, inline, className, children, ...props}) => {
                        return inline ? (
                            <code style={{ backgroundColor: '#F5F0E8', color: '#C0722C', padding: '2px 6px', borderRadius: '4px', fontFamily: 'monospace', fontSize: '14px' }} {...props}>{children}</code>
                        ) : (
                            <pre style={{ backgroundColor: '#1E1E1E', color: '#D4D4D4', padding: '15px', borderRadius: '12px', overflowX: 'auto', fontFamily: 'monospace', fontSize: '13px', lineHeight: '1.5', margin: '10px 0' }}>
                                <code {...props}>{children}</code>
                            </pre>
                        );
                    },
                    p: ({node, children, ...props}) => {
                        // If paragraph text contains [BAR: X%]
                        const textContent = typeof children === 'string' ? children : '';
                        if (textContent.includes('[BAR:')) {
                            const match = textContent.match(/\[BAR:\s*(\d+)%?\]/);
                            if (match) {
                                const val = parseInt(match[1]);
                                const label = textContent.replace(/\[BAR:.*?\]/, '').trim();
                                return (
                                    <div style={{ margin: '15px 0', backgroundColor: '#fff', padding: '20px', borderRadius: '16px', border: '1px solid #F0E8DB' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', fontSize: '13px', fontWeight: '800', color: '#2C1A10', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                            <span>{label}</span>
                                            <span style={{ color: '#C0722C' }}>{val}%</span>
                                        </div>
                                        <div style={{ height: '10px', backgroundColor: '#F0E8DB', borderRadius: '5px', overflow: 'hidden' }}>
                                            <div style={{ height: '100%', width: `${val}%`, background: 'linear-gradient(90deg, #C0722C 0%, #Eead7b 100%)' }} />
                                        </div>
                                    </div>
                                );
                            }
                        }
                        if (Array.isArray(children)) {
                            for (let child of children) {
                                if (typeof child === 'string' && child.includes('[BAR:')) {
                                    const match = child.match(/\[BAR:\s*(\d+)%?\]/);
                                    if (match) {
                                        const val = parseInt(match[1]);
                                        const label = children.map(c => typeof c === 'string' ? c : '').join('').replace(/\[BAR:.*?\]/, '').trim();
                                        return (
                                            <div style={{ margin: '15px 0', backgroundColor: '#fff', padding: '20px', borderRadius: '16px', border: '1px solid #F0E8DB' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', fontSize: '13px', fontWeight: '800', color: '#2C1A10', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                    <span>{label}</span>
                                                    <span style={{ color: '#C0722C' }}>{val}%</span>
                                                </div>
                                                <div style={{ height: '10px', backgroundColor: '#F0E8DB', borderRadius: '5px', overflow: 'hidden' }}>
                                                    <div style={{ height: '100%', width: `${val}%`, background: 'linear-gradient(90deg, #C0722C 0%, #Eead7b 100%)' }} />
                                                </div>
                                            </div>
                                        );
                                    }
                                }
                            }
                        }
                        return <p style={{ margin: '10px 0', color: '#555', fontSize: '15px', lineHeight: '1.8' }} {...props}>{children}</p>;
                    },
                    ul: ({node, ...props}) => <ul style={{ paddingLeft: '20px', margin: '10px 0', listStyleType: 'disc' }} {...props} />,
                    ol: ({node, ...props}) => <ol style={{ paddingLeft: '20px', margin: '10px 0', listStyleType: 'decimal' }} {...props} />,
                    li: ({node, ...props}) => <li style={{ margin: '6px 0', color: '#555', fontSize: '15px', lineHeight: '1.6' }} {...props} />,
                    blockquote: ({node, ...props}) => (
                        <blockquote style={{ borderLeft: '4px solid #C0722C', paddingLeft: '15px', margin: '15px 0', color: '#666', fontStyle: 'italic', backgroundColor: '#FAF6F0', padding: '10px 15px', borderRadius: '4px' }} {...props} />
                    ),
                    strong: ({node, ...props}) => <strong style={{ color: '#2C1A10', fontWeight: '800' }} {...props} />
                }}
            >
                {text}
            </ReactMarkdown>
        </div>
    );
};

const MinisterAIAssistant = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [analysis, setAnalysis] = useState('');
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('');
    const [isHovered, setIsHovered] = useState(false);

    const runAnalysis = async (type) => {
        setLoading(true);
        setActiveTab(type);
        setAnalysis('');
        try {
            const token = localStorage.getItem('token');
            const { data } = await axios.post(`${API_BASE_URL}/api/ai/analyze`, { type }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setAnalysis(data.analysis);
        } catch (err) {
            setAnalysis("Failed to synchronize with Ministerial AI engine. Please verify connectivity.");
        } finally {
            setLoading(false);
        }
    };

    const features = [
        { id: 'min_questions', name: 'Question Bank Audit', icon: <RiQuillPenFill />, color: '#C0722C' },
        { id: 'min_agendas', name: 'Legislation Roadmap', icon: <RiFileChartFill />, color: '#2E7D32' },
        { id: 'min_performance', name: 'Portfolio Pulse', icon: <RiFlowChart />, color: '#1565C0' },
        { id: 'min_meetings', name: 'Strategic Briefing', icon: <RiGroupFill />, color: '#2C1A10' },
        { id: 'min_staff', name: 'Departmental Staff Audit', icon: <RiShieldFlashFill />, color: '#D32F2F' }
    ];

    return (
        <>
            {/* Floating Action Button */}
            <div
                style={{
                    position: 'fixed',
                    bottom: '40px',
                    right: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '15px',
                    zIndex: 1000,
                    pointerEvents: 'none'
                }}
            >
                {/* Hover Label */}
                <AnimatePresence>
                    {isHovered && (
                        <motion.div
                            initial={{ opacity: 0, x: 20, scale: 0.8 }}
                            animate={{ opacity: 1, x: 0, scale: 1 }}
                            exit={{ opacity: 0, x: 20, scale: 0.8 }}
                            style={{
                                backgroundColor: 'rgba(44, 26, 16, 0.9)',
                                color: 'white',
                                padding: '12px 20px',
                                borderRadius: '16px',
                                fontSize: '14px',
                                fontWeight: '800',
                                letterSpacing: '1px',
                                whiteSpace: 'nowrap',
                                boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                                backdropFilter: 'blur(10px)',
                                border: '1px solid rgba(192, 114, 44, 0.3)',
                                pointerEvents: 'auto'
                            }}
                        >
                            AI INTELLIGENCE
                        </motion.div>
                    )}
                </AnimatePresence>

                <motion.div
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                    onClick={() => setIsOpen(true)}
                    style={{
                        width: '70px',
                        height: '70px',
                        backgroundColor: '#C0722C',
                        borderRadius: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '32px',
                        cursor: 'pointer',
                        boxShadow: '0 15px 45px rgba(192, 114, 44, 0.4)',
                        border: '2px solid rgba(255,255,255,0.2)',
                        pointerEvents: 'auto'
                    }}
                >
                    <RiRobot2Fill />
                </motion.div>
            </div>

            {/* AI Interface Drawer */}
            <AnimatePresence>
                {isOpen && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setIsOpen(false)}
                            style={{
                                position: 'fixed',
                                inset: 0,
                                backgroundColor: 'rgba(44, 26, 16, 0.4)',
                                backdropFilter: 'blur(10px)',
                                zIndex: 1001
                            }}
                        />
                        <motion.div
                            initial={{ x: '100%' }}
                            animate={{ x: 0 }}
                            exit={{ x: '100%' }}
                            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                            style={{
                                position: 'fixed',
                                top: '20px',
                                right: '20px',
                                bottom: '20px',
                                width: '500px',
                                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                backdropFilter: 'blur(20px)',
                                borderRadius: '32px',
                                zIndex: 1002,
                                boxShadow: '-20px 0 60px rgba(0,0,0,0.1)',
                                display: 'flex',
                                flexDirection: 'column',
                                overflow: 'hidden',
                                border: '1px solid #F0E8DB'
                            }}
                        >
                            {/* Header */}
                            <div style={{ padding: '40px', backgroundColor: '#FFF9F2', borderBottom: '1px solid #F0E8DB' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                        <div style={{ backgroundColor: '#C0722C', padding: '10px', borderRadius: '12px', color: 'white', display: 'flex' }}>
                                            <RiRobot2Fill size={24} />
                                        </div>
                                        <h2 style={{ margin: 0, color: '#2C1A10', fontWeight: '800', fontSize: '22px' }}>Ministerial AI</h2>
                                    </div>
                                    <motion.button
                                        whileHover={{ rotate: 90 }}
                                        onClick={() => setIsOpen(false)}
                                        style={{ background: 'none', border: 'none', color: '#999', cursor: 'pointer', display: 'flex' }}
                                    >
                                        <RiCloseLine size={32} />
                                    </motion.button>
                                </div>
                                <p style={{ margin: 0, color: '#777', fontSize: '14px', lineHeight: '1.5' }}>
                                    Strategic Portfolio Intelligence for Bihar Vidhan Sabha.
                                </p>
                            </div>

                            {/* Content */}
                            <div style={{ flex: 1, padding: '40px', overflowY: 'auto' }}>
                                {!analysis && !loading ? (
                                    <div style={{ animation: 'fadeIn 0.5s ease' }}>
                                        <h3 style={{ fontSize: '12px', fontWeight: '800', color: '#C0722C', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '25px' }}>
                                            Select Portfolio Core
                                        </h3>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '15px' }}>
                                            {features.map((f) => (
                                                <motion.button
                                                    key={f.id}
                                                    whileHover={{ x: 10, backgroundColor: '#FFF9F2' }}
                                                    onClick={() => runAnalysis(f.id)}
                                                    style={{
                                                        padding: '24px',
                                                        backgroundColor: 'white',
                                                        border: '1px solid #F0E8DB',
                                                        borderRadius: '20px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '20px',
                                                        cursor: 'pointer',
                                                        textAlign: 'left',
                                                        transition: 'all 0.2s'
                                                    }}
                                                >
                                                    <div style={{ color: f.color, fontSize: '24px', display: 'flex' }}>{f.icon}</div>
                                                    <div style={{ fontWeight: '700', color: '#2C1A10', fontSize: '16px' }}>{f.name}</div>
                                                </motion.button>
                                            ))}
                                        </div>
                                    </div>
                                ) : (
                                    <div style={{ animation: 'fadeIn 0.3s ease' }}>
                                        <button 
                                            onClick={() => { setAnalysis(''); setActiveTab(''); }}
                                            style={{ color: '#C0722C', background: 'none', border: 'none', fontWeight: '700', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer', marginBottom: '25px', padding: 0 }}
                                        >
                                            ← Back to Portfolio selection
                                        </button>
                                        
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                                            <div style={{ color: features.find(f => f.id === activeTab)?.color, fontSize: '20px', display: 'flex' }}>
                                                {features.find(f => f.id === activeTab)?.icon}
                                            </div>
                                            <h4 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: '#2C1A10' }}>
                                                {features.find(f => f.id === activeTab)?.name}
                                            </h4>
                                        </div>

                                        {loading ? (
                                            <div style={{ padding: '60px 0', textAlign: 'center' }}>
                                                <motion.div
                                                    animate={{ rotate: 360 }}
                                                    transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}
                                                    style={{ width: '40px', height: '40px', border: '3px solid #F0E8DB', borderTopColor: '#C0722C', borderRadius: '50%', margin: '0 auto 20px' }}
                                                />
                                                <p style={{ color: '#888', fontWeight: '600', fontSize: '14px' }}>Synthesizing ministerial data...</p>
                                            </div>
                                        ) : (
                                            <div style={{ 
                                                backgroundColor: '#F9F9F9', 
                                                padding: '30px', 
                                                borderRadius: '24px', 
                                                color: '#444', 
                                                border: '1px solid #F0F0F0'
                                            }}>
                                                <FormattedAIResponse text={analysis} />
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Footer */}
                            <div style={{ padding: '25px 40px', backgroundColor: '#fff', borderTop: '1px solid #F0E8DB', fontSize: '11px', color: '#999', textAlign: 'center', fontWeight: '600', letterSpacing: '0.5px' }}>
                                POWERED BY BIHAR VIDHAN SABHA SECURE AI GATEWAY
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </>
    );
};

export default MinisterAIAssistant;
export { FormattedAIResponse };
